import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Download, Search, Trash2, Mail, FileText, Send, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { BOOKING_STATUSES } from "@/lib/constants";
import { StatusBadge, Panel, AdminSelect, Empty } from "@/pages/admin/adminui";
import { LuxButton } from "@/components/lux";
import { Input } from "@/components/ui/input";

const to12h = (t) => { if (!t) return "—"; const [h, m] = t.split(":").map(Number); if (Number.isNaN(h)) return t; const ap = h >= 12 ? "PM" : "AM"; const hr = ((h + 11) % 12) + 1; return `${hr}:${String(m).padStart(2, "0")} ${ap}`; };

export default function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [status, setStatus] = useState("ALL");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);

  const load = () => {
    const params = { sort: "created_at" };
    if (status !== "ALL") params.status = status;
    if (q) params.q = q;
    api.get("/admin/bookings", { params }).then((r) => setBookings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [status, q]); // eslint-disable-line

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/bookings-export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "luhai_bookings.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <Panel testid="bookings-tab" title="Bookings" action={
      <LuxButton size="sm" variant="outline" onClick={exportCsv} data-testid="export-bookings"><Download size={14} /> Export</LuxButton>
    }>
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, reference…" data-testid="search-bookings"
            className="pl-9 bg-[#0b0d12] border-white/10 text-white focus-visible:ring-[#02AACF]" />
        </div>
        <div className="sm:w-52">
          <AdminSelect value={status} onChange={(e) => setStatus(e.target.value)} options={["ALL", ...BOOKING_STATUSES]} testid="filter-status" />
        </div>
      </div>

      {bookings.length === 0 ? <Empty>No bookings found.</Empty> : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-[#64748B] font-mono2 text-xs uppercase tracking-wide">
                <th className="px-3 py-2">Ref</th><th className="px-3 py-2">Client</th><th className="px-3 py-2">Package</th>
                <th className="px-3 py-2">Date</th><th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} onClick={() => setActive(b)} data-testid={`booking-row-${b.reference}`}
                  className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer">
                  <td className="px-3 py-3 font-mono2 text-[#02AACF] text-xs">{b.reference}</td>
                  <td className="px-3 py-3 text-white">{b.full_name}<div className="text-[#64748B] text-xs">{b.email}</div></td>
                  <td className="px-3 py-3 text-[#cbd5e1]">{b.package_name || "—"}</td>
                  <td className="px-3 py-3 text-[#cbd5e1]">{b.shoot_date || "—"}<div className="text-[#64748B] text-xs">{b.shoot_time}</div></td>
                  <td className="px-3 py-3"><StatusBadge status={b.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BookingDetail booking={active} onClose={() => setActive(null)} onChanged={() => { load(); setActive(null); }} />
    </Panel>
  );
}

function BookingDetail({ booking, onClose, onChanged }) {
  const [status, setStatus] = useState("");
  const [payment, setPayment] = useState("");
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (booking) {
      setStatus(booking.status); setPayment(booking.payment_status || "UNPAID");
      setNotes(booking.admin_notes || ""); setDate(booking.shoot_date || ""); setTime(booking.shoot_time || "");
    }
  }, [booking]);

  if (!booking) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/bookings/${booking.id}`, {
        status, payment_status: payment, admin_notes: notes, shoot_date: date, shoot_time: time, send_email: sendEmail,
      });
      toast.success("Booking updated" + (sendEmail && status !== booking.status ? " · email sent" : ""));
      onChanged();
    } catch { toast.error("Update failed"); } finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this booking permanently?")) return;
    await api.delete(`/admin/bookings/${booking.id}`);
    toast.success("Deleted"); onChanged();
  };

  const fields = [["Email", booking.email], ["Phone", booking.phone], ["Contact", booking.preferred_contact],
    ["Location", booking.location], ["People", booking.num_people], ["Outfits", booking.num_outfits],
    ["Shoot Type", booking.shoot_type], ["Instagram", booking.instagram], ["Brand", booking.business_name],
    ["Heard via", booking.heard_about], ["Email status", booking.email_notification_status]];

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#12141C] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-detail">
        <DialogTitle className="sr-only">Booking details</DialogTitle>
        <div className="flex items-center justify-between pr-8">
          <div>
            <p className="font-mono2 text-[#02AACF] text-xs">{booking.reference}</p>
            <h3 className="font-display font-bold text-xl mt-1">{booking.full_name}</h3>
          </div>
          <StatusBadge status={booking.status} />
        </div>

        <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-4 text-sm">
          {fields.map(([l, v]) => v ? (
            <div key={l} className="flex flex-col"><span className="text-[#64748B] text-xs font-mono2">{l}</span><span className="text-[#e2e8f0]">{v}</span></div>
          ) : null)}
        </div>
        {booking.vision && <div className="mt-3"><span className="text-[#64748B] text-xs font-mono2">Vision</span><p className="text-[#cbd5e1] text-sm">{booking.vision}</p></div>}
        {booking.notes && <div className="mt-2"><span className="text-[#64748B] text-xs font-mono2">Notes</span><p className="text-[#cbd5e1] text-sm">{booking.notes}</p></div>}
        {booking.reference_links && <div className="mt-2"><span className="text-[#64748B] text-xs font-mono2">Links</span><p className="text-[#02AACF] text-sm break-all">{booking.reference_links}</p></div>}
        {booking.uploaded_inspiration?.length > 0 && (
          <div className="mt-3">
            <span className="text-[#64748B] text-xs font-mono2">Inspiration Uploads</span>
            <div className="grid grid-cols-5 gap-2 mt-1.5" data-testid="booking-inspiration">
              {booking.uploaded_inspiration.map((u, i) => (
                <a key={i} href={u} target="_blank" rel="noreferrer" className="rounded-lg overflow-hidden border border-white/10 aspect-square">
                  <img src={u} alt="reference" className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          </div>
        )}

        {(booking.is_custom_time || booking.status === "PENDING CUSTOM TIME" || booking.status === "TIME SUGGESTED") && (
          <>
            <div className="h-px bg-white/10 my-5" />
            <CustomTimePanel booking={booking} onChanged={onChanged} />
          </>
        )}
        <div className="h-px bg-white/10 my-5" />
        <WaveInvoice booking={booking} />
        <div className="h-px bg-white/10 my-5" />
        <div className="grid sm:grid-cols-2 gap-4">
          <AdminSelect label="Status" value={status} onChange={(e) => setStatus(e.target.value)} options={BOOKING_STATUSES} testid="update-status" />
          <AdminSelect label="Payment" value={payment} onChange={(e) => setPayment(e.target.value)} options={["UNPAID", "DEPOSIT PAID", "PAID IN FULL", "REFUNDED"]} testid="update-payment" />
          <div><label className="text-[#94A3B8] text-xs font-mono2">Date</label><Input value={date} onChange={(e) => setDate(e.target.value)} placeholder="YYYY-MM-DD" data-testid="update-date" className="mt-1.5 bg-[#0b0d12] border-white/10 text-white" /></div>
          <div><label className="text-[#94A3B8] text-xs font-mono2">Time</label><Input value={time} onChange={(e) => setTime(e.target.value)} placeholder="HH:MM" data-testid="update-time" className="mt-1.5 bg-[#0b0d12] border-white/10 text-white" /></div>
        </div>
        <div className="mt-4">
          <label className="text-[#94A3B8] text-xs font-mono2">Admin Notes</label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="update-notes" className="mt-1.5 bg-[#0b0d12] border-white/10 text-white" />
        </div>
        <label className="flex items-center gap-2 mt-4 text-sm text-[#cbd5e1] cursor-pointer">
          <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="send-email-toggle" className="accent-[#02AACF]" />
          <Mail size={14} /> Send status email to client on change
        </label>

        <div className="flex justify-between mt-6">
          <LuxButton variant="ghost" onClick={del} data-testid="delete-booking" className="text-[#f87171]"><Trash2 size={14} /> Delete</LuxButton>
          <LuxButton onClick={save} disabled={saving} data-testid="save-booking">{saving ? "Saving…" : "Save Changes"}</LuxButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function WaveInvoice({ booking }) {
  const [w, setW] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    setW(booking?.wave_invoice_id ? {
      id: booking.wave_invoice_id, number: booking.wave_invoice_number, status: booking.wave_status,
      total: booking.wave_total, currency: booking.wave_currency, pdf: booking.wave_pdf_url, view: booking.wave_view_url,
      emailed: booking.invoice_email_status === "Sent",
    } : null);
  }, [booking]);

  const create = async (confirm = false) => {
    setBusy("create");
    try {
      const r = await api.post(`/admin/bookings/${booking.id}/wave-invoice`, null, { params: { confirm } });
      setW({ id: r.data.wave_invoice_id, number: r.data.wave_invoice_number, status: r.data.wave_status,
        total: r.data.wave_total, currency: r.data.wave_currency, pdf: r.data.wave_pdf_url, view: r.data.wave_view_url });
      toast.success(`Wave invoice ${r.data.wave_invoice_number || ""} created`);
    } catch (e) {
      if (e?.response?.status === 409) {
        setBusy("");
        if (window.confirm("An invoice already exists for this booking. Create another anyway?")) return create(true);
        return;
      }
      toast.error(e?.response?.data?.detail || "Failed to create Wave invoice");
    } finally { setBusy(""); }
  };

  const send = async () => {
    setBusy("send");
    try {
      await api.post(`/admin/bookings/${booking.id}/wave-invoice/send`);
      setW((x) => ({ ...x, emailed: true }));
      toast.success("Invoice email sent to the client");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send invoice email"); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="wave-invoice-section">
      <div className="flex items-center justify-between">
        <p className="font-mono2 text-xs tracking-[0.25em] text-[#02AACF] uppercase">Wave Invoice</p>
        {w?.status && <span className="text-xs font-mono2 px-2 py-1 rounded-full border border-white/15 text-[#cbd5e1]" data-testid="wave-invoice-status">{w.status}</span>}
        {w?.emailed && <span className="ml-2 text-xs font-mono2 px-2 py-1 rounded-full border border-emerald-500/40 text-emerald-300" data-testid="wave-emailed-chip">Emailed</span>}
      </div>
      {!w ? (
        <LuxButton size="sm" className="mt-3" onClick={() => create(false)} disabled={busy === "create"} data-testid="create-wave-invoice">
          <FileText size={14} /> {busy === "create" ? "Creating…" : "Create Wave Invoice"}
        </LuxButton>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
            <div className="flex flex-col"><span className="text-[#64748B] text-xs font-mono2">Invoice #</span><span className="text-white" data-testid="wave-invoice-number">{w.number || "—"}</span></div>
            <div className="flex flex-col"><span className="text-[#64748B] text-xs font-mono2">Total</span><span className="text-white" data-testid="wave-invoice-total">{w.total ? `${w.currency || ""} ${w.total}` : "—"}</span></div>
          </div>
          <div className="flex flex-wrap gap-2">
            <LuxButton size="sm" variant="outline" onClick={() => window.open(w.view, "_blank")} disabled={!w.view} data-testid="wave-view-invoice"><ExternalLink size={14} /> View Invoice</LuxButton>
            <LuxButton size="sm" variant="outline" onClick={() => window.open(w.pdf, "_blank")} disabled={!w.pdf} data-testid="wave-download-pdf"><Download size={14} /> Download PDF</LuxButton>
            <LuxButton size="sm" onClick={send} disabled={busy === "send"} data-testid="wave-send-invoice"><Send size={14} /> {busy === "send" ? "Sending…" : (w.emailed ? "Resend Invoice Email" : "Send Invoice Email")}</LuxButton>
          </div>
          <button onClick={() => create(true)} disabled={busy === "create"} className="text-xs text-[#64748B] hover:text-[#02AACF] font-mono2" data-testid="wave-create-another">+ Create another invoice</button>
        </div>
      )}
    </div>
  );
}


function CustomTimePanel({ booking, onChanged }) {
  const [busy, setBusy] = useState("");
  const [suggest, setSuggest] = useState(false);
  const [time, setTime] = useState("");
  const [note, setNote] = useState("");

  const act = async (action, extra = {}) => {
    setBusy(action);
    try {
      await api.post(`/admin/bookings/${booking.id}/custom-time`, { action, ...extra });
      toast.success(action === "approve" ? "Approved & confirmed · email sent" : action === "decline" ? "Declined · email sent" : "Suggestion sent to client");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(""); }
  };

  return (
    <div className="rounded-xl border border-[#f59e0b]/40 bg-[#f59e0b]/10 p-4" data-testid="custom-time-panel">
      <p className="font-mono2 text-xs tracking-[0.25em] text-[#f59e0b] uppercase">Custom Time Request</p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm mt-2">
        <div className="flex flex-col"><span className="text-[#64748B] text-xs font-mono2">Requested Date</span><span className="text-[#e2e8f0]">{booking.shoot_date || "—"}</span></div>
        <div className="flex flex-col"><span className="text-[#64748B] text-xs font-mono2">Requested Time</span><span className="text-[#e2e8f0]" data-testid="custom-requested-time">{to12h(booking.shoot_time)} <span className="text-[#64748B] text-xs">(Nassau)</span></span></div>
        {booking.suggested_time && <div className="flex flex-col"><span className="text-[#64748B] text-xs font-mono2">Suggested Time</span><span className="text-[#02AACF]">{to12h(booking.suggested_time)}</span></div>}
      </div>
      {booking.custom_reason && <p className="text-[#cbd5e1] text-sm mt-2"><span className="text-[#64748B] text-xs font-mono2">Reason: </span>{booking.custom_reason}</p>}
      <div className="flex flex-wrap gap-2 mt-4">
        <LuxButton size="sm" onClick={() => act("approve")} disabled={!!busy} data-testid="custom-approve">{busy === "approve" ? "Approving…" : "Approve"}</LuxButton>
        <LuxButton size="sm" variant="outline" onClick={() => act("decline")} disabled={!!busy} data-testid="custom-decline">{busy === "decline" ? "Declining…" : "Decline"}</LuxButton>
        <LuxButton size="sm" variant="outline" onClick={() => setSuggest((v) => !v)} disabled={!!busy} data-testid="custom-suggest-toggle">Suggest Another Time</LuxButton>
      </div>
      {suggest && (
        <div className="mt-3 flex flex-wrap items-end gap-2" data-testid="custom-suggest-form">
          <div>
            <label className="text-[#94A3B8] text-xs font-mono2">Suggested time (Nassau)</label>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="custom-suggest-time" className="mt-1.5 bg-[#0b0d12] border-white/10 text-white" />
          </div>
          <Input placeholder="Optional note to client" value={note} onChange={(e) => setNote(e.target.value)} data-testid="custom-suggest-note" className="flex-1 min-w-[160px] bg-[#0b0d12] border-white/10 text-white" />
          <LuxButton size="sm" onClick={() => time ? act("suggest", { suggested_time: time, note }) : toast.error("Pick a suggested time")} disabled={!!busy} data-testid="custom-suggest-send">{busy === "suggest" ? "Sending…" : "Send Suggestion"}</LuxButton>
        </div>
      )}
    </div>
  );
}

